import React from "react";
import styled from "styled-components";
import { Card, Row, Col, Statistic, Divider } from "antd";
import {
  DollarOutlined,
  LockOutlined,
  ClockCircleOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

const ExposureCard = styled(Card)`
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  height: 100%;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .ant-card-head-title {
    font-weight: 600;
    color: #005249;
  }

  .ant-card-body {
    padding: 24px;
  }
`;

const StatisticItem = styled.div`
  text-align: center;
`;

const FinancialExposureCard = ({ financialExposure }) => {
  const navigate = useNavigate();

  const exposure = financialExposure || {
    availableBalance: 0,
    heldAmount: 0,
    pendingPayouts: 0,
    adjustmentsThisWeek: 0,
    currency: "ZAR",
    asOf: new Date().toISOString(),
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-ZA", {
      style: "currency",
      currency: exposure.currency || "ZAR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch {
      return "N/A";
    }
  };

  return (
    <ExposureCard
      title="Financial Exposure"
      extra={
        <span style={{ fontSize: "12px", color: "#8c8c8c" }}>
          As of {formatDate(exposure.asOf)}
        </span>
      }
      onClick={() => navigate("/finance")}
    >
      <Row gutter={[16, 16]}>
        <Col xs={12} sm={12} lg={6}>
          <StatisticItem>
            <Statistic
              title="Available Balance"
              value={exposure.availableBalance}
              prefix={<DollarOutlined />}
              valueStyle={{
                fontSize: "20px",
                fontWeight: 600,
                color: exposure.availableBalance > 0 ? "#52c41a" : "#8c8c8c",
              }}
              formatter={(value) => formatCurrency(value)}
            />
          </StatisticItem>
        </Col>

        <Col xs={12} sm={12} lg={6}>
          <StatisticItem>
            <Statistic
              title="Held Amount"
              value={exposure.heldAmount}
              prefix={<LockOutlined />}
              valueStyle={{
                fontSize: "20px",
                fontWeight: 600,
                color: exposure.heldAmount > 0 ? "#ff4d4f" : "#8c8c8c",
              }}
              formatter={(value) => formatCurrency(value)}
            />
          </StatisticItem>
        </Col>

        <Col xs={12} sm={12} lg={6}>
          <StatisticItem>
            <Statistic
              title="Pending Payouts"
              value={exposure.pendingPayouts}
              prefix={<ClockCircleOutlined />}
              valueStyle={{
                fontSize: "20px",
                fontWeight: 600,
                color: exposure.pendingPayouts > 0 ? "#faad14" : "#8c8c8c",
              }}
              formatter={(value) => formatCurrency(value)}
            />
          </StatisticItem>
        </Col>

        <Col xs={12} sm={12} lg={6}>
          <StatisticItem>
            <Statistic
              title="Adjustments This Week"
              value={exposure.adjustmentsThisWeek}
              prefix={<EditOutlined />}
              valueStyle={{
                fontSize: "20px",
                fontWeight: 600,
                color: exposure.adjustmentsThisWeek !== 0 ? "#1890ff" : "#8c8c8c",
              }}
              formatter={(value) => formatCurrency(Math.abs(value))}
            />
          </StatisticItem>
        </Col>
      </Row>
    </ExposureCard>
  );
};

export default FinancialExposureCard;
